import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';

import ExitConfirmModal from './ExitConfirmModal';
import { useDiscord } from '../../../discord/useDiscord';
import { createChannelLobby } from '../../../discord/DiscordProvider';

import './main-menu.css';
import { LOGO_URL } from '../../../Settings';


// CC_FE_GAPS (2026-06-01): bound the open-web New Game wait for the
// group-update-event that carries the new lobby's uuid. Mirrors
// LobbiesPage. If nothing arrives in this window we drop the listener
// and stay on the Main Menu rather than navigate somewhere wrong.
const NEW_GAME_NAV_TIMEOUT_MS = 3000;


/**
 * CC_MAIN_MENU (2026-05-27) / CC_FE_PAGES (2026-06-01) /
 * CC_FE_GAPS (2026-06-01).
 *
 * Canonical landing surface for both browser and Discord-embedded
 * users. A single vertical button list:
 *
 *   Resume | New Game | Find a Game | Saved Games | Profile |
 *   Rankings | Configure | Exit
 *
 * CC_GAMES_LIST_DOOR (2026-06-08): added Find a Game — the entrance to
 * the public games list at `/lobbies` (LobbiesPage), which had no
 * navigation into it. Routes via quick-router-event like the siblings.
 *
 * CC_RANKINGS_DOOR (2026-06-08): added Rankings — the entrance to the
 * rankings/leaderboard at `/rankings` (RankingsShell → RankingsPage).
 * The page was fully built and routed (App.js) but, like the games
 * list, had no navigation into it. Routes via quick-router-event like
 * the siblings. Profile (the pre-existing `/profile` door) stays put.
 *
 * CC_FE_PAGES moved the LiveTile grid to `/lobbies` (LobbiesPage) and
 * the Saved Games list to `/saves` (SavesPage). The Main Menu ROUTES to
 * `/saves` for Resume / Saved Games.
 *
 * CC_FE_GAPS: New Game is now a one-click create-and-go — it no longer
 * detours through `/lobbies`. It creates the lobby directly (Discord:
 * createChannelLobby; open-web: joinGroup(0)) and navigates to
 * `/game/:lobbyUuid` once the uuid is in hand. The create path is
 * "wherever the user clicks New Game" — both MainMenu and LobbiesPage
 * fire create; LobbiesPage's own New Game button stays for users who
 * arrive there directly.
 *
 * Configure delegates to the `app.configurationMenu` singleton mounted
 * by BottomMenu (matching the EscapeMenu pattern). Exit branches via
 * <ExitConfirmModal>. `inProgressCount` (from `list-update-event`) only
 * gates the Resume button's disabled state.
 */
export default function MainMenu({ app }) {
    const discord = useDiscord();
    const channelId = discord.channelId;
    const guildId = discord.guildId;
    const instanceId = discord.instanceId;
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [inProgressCount, setInProgressCount] = useState(0);

    useEffect(() => {
        const handler = (event) => {
            const data = (event && event.detail) || {};
            const mine = Array.isArray(data.games_list)
                ? data.games_list : [];
            const joined = Array.isArray(data.joined_games)
                ? data.joined_games : [];
            setInProgressCount(mine.length + joined.length);
        };
        window.addEventListener('list-update-event', handler);
        if (app && app.game && app.game.gameApi) {
            app.game.gameApi.listGames();
        }
        return () => {
            window.removeEventListener('list-update-event', handler);
        };
    }, [app]);

    const onResume = useCallback(() => {
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/saves' }));
    }, []);

    const onNewGame = useCallback(() => {
        // CC_FE_GAPS (2026-06-01): one-click create-and-go. Mirrors
        // LobbiesPage's onNewGame so both surfaces create the same way.
        if (discord.isEmbedded && channelId) {
            // Discord: mint a fresh channel-bound lobby, then route to
            // its uuid. createChannelLobby returns the seated payload and
            // also fires discord-lobby-joined, which GamePage reads.
            createChannelLobby(channelId, guildId, instanceId)
                .then((payload) => {
                    const uuid = payload && payload.lobby
                        && payload.lobby.uuid;
                    if (uuid) {
                        window.dispatchEvent(new CustomEvent(
                            'quick-router-event',
                            { detail: '/game/' + uuid }));
                    }
                });
            return;
        }
        // Open web: mint a fresh Browser-bound lobby on the WS rail. The
        // Members/Join broadcast carries the lobby uuid (surfaced on
        // group-update-event by GameGroupHandler), so install a one-shot
        // listener BEFORE firing Join(0) and route to /game/:lobbyUuid
        // off the next broadcast. Bound the wait so a silent backend
        // leaves the user on the Main Menu rather than stranded.
        if (!(app && app.game && app.game.gameApi)) {
            return;
        }
        let timeoutId = null;
        const onGroupUpdate = (event) => {
            const detail = (event && event.detail) || {};
            if (!detail.lobbyUuid) {
                return;
            }
            window.removeEventListener('group-update-event', onGroupUpdate);
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            window.dispatchEvent(new CustomEvent(
                'quick-router-event',
                { detail: '/game/' + detail.lobbyUuid }));
        };
        window.addEventListener('group-update-event', onGroupUpdate);
        timeoutId = setTimeout(() => {
            window.removeEventListener('group-update-event', onGroupUpdate);
            console.warn('[MainMenu] New Game: no group-update-event with'
                + ' lobbyUuid within %sms — staying on Main Menu',
                NEW_GAME_NAV_TIMEOUT_MS);
        }, NEW_GAME_NAV_TIMEOUT_MS);
        app.game.gameApi.joinGroup(0);
    }, [discord.isEmbedded, channelId, guildId, instanceId, app]);

    const onFindGame = useCallback(() => {
        // CC_GAMES_LIST_DOOR (2026-06-08): the entrance to the public
        // games list. /lobbies (LobbiesPage) is the full public-games
        // surface but nothing navigated here. Rides quick-router-event
        // like the sibling buttons. Works on both platforms:
        // DiscordLobbyNavigator is a one-shot boot redirect (didFireRef
        // latch, mounted outside <Routes>), not a persistent guard, so
        // it does not bounce a Discord user back off /lobbies.
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/lobbies' }));
    }, []);

    const onSavedGames = useCallback(() => {
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/saves' }));
    }, []);

    const onProfile = useCallback(() => {
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/profile' }));
    }, []);

    const onRankings = useCallback(() => {
        // CC_RANKINGS_DOOR (2026-06-08): the entrance to the
        // rankings/leaderboard. /rankings (RankingsShell → RankingsPage)
        // is the full public rankings surface but nothing navigated here
        // — same missing-door as the games list. Rides quick-router-event
        // like the sibling buttons; works on both platforms (same routed
        // session/origin as /lobbies and /profile).
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/rankings' }));
    }, []);

    const onConfigure = useCallback(() => {
        // ConfigurationMenu is mounted as a singleton by BottomMenu
        // and stashes itself on app.configurationMenu in its
        // constructor. Same opening path EscapeMenu uses.
        if (app && app.configurationMenu) {
            app.configurationMenu.show();
        }
    }, [app]);

    const onExit = useCallback(() => setShowExitConfirm(true), []);

    return (
        <div className="main-menu">
            <div><img
                src={LOGO_URL}
                width={'50%'}
                height={'auto'}
                alt={'hsh-logo'}
                style={{
                    display: 'block',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    width: '15vw',
                    paddingBottom: '10px',
                }}
            /></div>
            <div className="main-menu-buttons">
                <Button
                    variant="seed"
                    className="main-menu-item"
                    disabled={inProgressCount === 0}
                    onClick={onResume}
                    title={inProgressCount === 0
                        ? 'No saved games to resume.'
                        : `Resume one of your ${inProgressCount} saved games.`}
                >
                    Resume
                </Button>
                <Button
                    variant="hex"
                    className="main-menu-item"
                    onClick={onNewGame}
                >
                    New Game
                </Button>
                <Button
                    variant="stem"
                    className="main-menu-item"
                    onClick={onFindGame}
                >
                    {/* COPY PLACEHOLDER — "Find a Game" pending Michael's
                        word (alt: "Browse Games"). */}
                    Find a Game
                </Button>
                <Button
                    variant="honored"
                    className="main-menu-item"
                    onClick={onSavedGames}
                >
                    Saved Games
                </Button>
                {/*<Button*/}
                {/*    variant="deep"*/}
                {/*    className="main-menu-item"*/}
                {/*    disabled*/}
                {/*    title="Coming soon"*/}
                {/*>*/}
                {/*    Campaign*/}
                {/*</Button>*/}
                {/*<Button*/}
                {/*    variant="stem"*/}
                {/*    className="main-menu-item"*/}
                {/*    disabled*/}
                {/*    title="Coming soon"*/}
                {/*>*/}
                {/*    Daily*/}
                {/*</Button>*/}
                <Button
                    variant="deep"
                    className="main-menu-item"
                    onClick={onProfile}
                >
                    Profile
                </Button>
                <Button
                    variant="honored"
                    className="main-menu-item"
                    onClick={onRankings}
                >
                    {/* COPY PLACEHOLDER — "Rankings" pending Michael's
                        word (alt: "Leaderboard"). */}
                    Rankings
                </Button>
                <Button
                    variant="dread"
                    className="main-menu-item"
                    onClick={onConfigure}
                >
                    Configure
                </Button>
                <Button
                    variant="outline-dread"
                    className="main-menu-item main-menu-exit"
                    onClick={onExit}
                >
                    Exit
                </Button>
            </div>

            <ExitConfirmModal
                app={app}
                show={showExitConfirm}
                onClose={() => setShowExitConfirm(false)}
            />
        </div>
    );
}
